import React, {
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

import SortSavedItemsModal from './SortSavedItemsModal'
import ItemCard from '../ItemCard/ItemCard'

import FilterChip from 'components/Luxkit/Chips/FilterChip'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import Dropdown from 'components/Luxkit/Dropdown/v2/Dropdown'
import LineAngleDownIcon from 'components/Luxkit/Icons/line/LineAngleDownIcon'
import ListItem from 'components/Luxkit/List/ListItem'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import useToggle from 'hooks/useToggle'
import { sortBy } from 'lib/arrayUtils'
import { capitalise } from 'lib/stringUtils'
import { SAVED_ITEMS_LIST_LAST_SELECTED_SORT_OPTION_STORAGE_KEY } from 'tripPlanner/config'
import { BasicTrip } from 'tripPlanner/types/common'
import { TripItem } from 'tripPlanner/types/tripItem'
import { filterProducts, filterStatus } from 'tripPlanner/utils/summary'

export const TRIP_ITEMS_SORT_OPTIONS = [
  'recently added',
  'alphabetical',
  'by trip',
] as const

export type TripItemsSortOptions = (typeof TRIP_ITEMS_SORT_OPTIONS)[number]

interface Props {
  trips: BasicTrip[]
  tripItems: TripItem[]
  filters: App.TripPlannerSubFilterType[]
}

export default function SavedItemsList({ trips, filters, tripItems }: Props) {
  const [sortOption, setSortOption] =
    useState<TripItemsSortOptions>('recently added')
  const sortChipRef = useRef(null)
  const { value: isDropdownOpen, toggle: toggleDropdown } = useToggle()

  const tripNameMap: Record<string, string> = useMemo(() => {
    return trips.reduce((map, trip) => {
      map[trip.id] = trip.name
      return map
    }, {})
  }, [trips])

  const showModal = useContext(ModalContext)

  const onApplySortOption = useCallback(
    (sortOption: TripItemsSortOptions) => {
      setSortOption(sortOption)
      toggleDropdown()
      localStorage.setItem(
        SAVED_ITEMS_LIST_LAST_SELECTED_SORT_OPTION_STORAGE_KEY,
        JSON.stringify(sortOption),
      )
    },
    [setSortOption, toggleDropdown],
  )

  const showSortItemsModal = useCallback(() => {
    showModal(
      <SortSavedItemsModal
        onSelect={onApplySortOption}
        selectedSortOption={sortOption}
      />,
    )
  }, [onApplySortOption, showModal, sortOption])

  const processedTripItems = useMemo(() => {
    let processed = tripItems ? [...tripItems] : []
    const filtersSet = new Set(filters)
    if (filters.length > 0) {
      processed = processed.filter(
        (item) =>
          filterProducts(item, filtersSet) || filterStatus(item, filtersSet),
      )
    }
    switch (sortOption) {
      case 'recently added':
        processed = sortBy(processed, (item) => item.createdAt, 'desc')
        break
      case 'alphabetical':
        processed = sortBy(processed, (item) => item.sortableName, 'asc')
        break
      case 'by trip':
        processed = sortBy(processed, (item) => tripNameMap[item.tripId], 'asc')
        break
    }
    return processed
  }, [filters, sortOption, tripItems, tripNameMap])

  // Intialise stored sort & filter options
  useIsomorphicLayoutEffect(() => {
    const storedSortOption = localStorage.getItem(
      SAVED_ITEMS_LIST_LAST_SELECTED_SORT_OPTION_STORAGE_KEY,
    )

    if (storedSortOption) {
      const parsedOption = JSON.parse(storedSortOption)
      if (TRIP_ITEMS_SORT_OPTIONS.includes(parsedOption)) {
        setSortOption(parsedOption)
      }
    }
  }, [])

  return (
    <Group direction="vertical" gap={0}>
      <Group direction="vertical" gap={24}>
        {processedTripItems.length > 1 && (
          <Group
            direction="horizontal"
            horizontalAlign="space-between"
            verticalAlign="center"
            gap={12}
          >
            <Group direction="horizontal" gap={12} verticalAlign="center">
              {
                // Placeholder for filter buttons
              }
            </Group>{' '}
            <CSSBreakpoint max="mobile">
              <FilterChip
                ref={sortChipRef}
                style={{ zIndex: 1 }}
                onClick={showSortItemsModal}
                size="medium"
                endIcon={<LineAngleDownIcon />}
              >
                Sort: {capitalise(sortOption)}
              </FilterChip>
            </CSSBreakpoint>
            <CSSBreakpoint min="tablet">
              <FilterChip
                ref={sortChipRef}
                style={{ zIndex: 1 }}
                onClick={toggleDropdown}
                size="medium"
                endIcon={<LineAngleDownIcon />}
              >
                Sort: {capitalise(sortOption)}
              </FilterChip>
              <Dropdown
                position="bottom"
                open={isDropdownOpen}
                triggerRef={sortChipRef}
                onClose={toggleDropdown}
              >
                <DropdownContainer fit="dropdown-items">
                  {TRIP_ITEMS_SORT_OPTIONS.map((opt) => (
                    <ListItem
                      key={opt}
                      onClick={() => onApplySortOption(opt)}
                      title={capitalise(opt)}
                      selected={sortOption === opt}
                    />
                  ))}
                </DropdownContainer>
              </Dropdown>
            </CSSBreakpoint>
          </Group>
        )}
        <Group direction="vertical" gap={24}>
          {processedTripItems.map((item) => (
            <ItemCard key={item.id} item={item} />
          ))}
        </Group>
      </Group>
    </Group>
  )
}
